import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { X, Heart, Send, Eye, Trash2, BarChart3, HelpCircle, Link2, Music, VolumeX, Volume2, Play } from "lucide-react";
import api from "@/lib/api";
import { fileUrl } from "@/lib/uploads";
import { bgByKey, STORY_FONTS, REACT_EMOJIS } from "@/lib/storyBg";
import { getInitials, avatarColor } from "@/lib/avatar";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

dayjs.extend(relativeTime);

export default function StoryViewer({ groups, startGroup = 0, onClose, onDeleted, readOnly = false }) {
  const { t } = useI18n();
  const [gi, setGi] = useState(startGroup);
  const [si, setSi] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reply, setReply] = useState("");
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [insights, setInsights] = useState(null);
  const [resp, setResp] = useState({});        // key -> {value, tally}
  const [qText, setQText] = useState({});       // overlay key -> input text
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const holdTimer = useRef(null);
  const startPt = useRef(null);
  const rafRef = useRef(null);
  const videoRef = useRef(null);
  const startedAt = useRef(0);
  const elapsedBefore = useRef(0);
  const [videoTap, setVideoTap] = useState(false); // autoplay blocked (native WebView) → show play button
  const [hearts, setHearts] = useState([]); // floating double-tap hearts {id,x,y}
  const lastTap = useRef({ t: 0, x: 0 });
  const tapTimer = useRef(null);

  const group = groups[gi];
  const story = group?.stories?.[si];
  const mine = !!group?.is_me;

  const goNextGroup = useCallback(() => {
    if (gi < groups.length - 1) { setGi(gi + 1); setSi(0); setProgress(0); }
    else onClose();
  }, [gi, groups.length, onClose]);

  const goNext = useCallback(() => {
    if (!group) return;
    if (si < group.stories.length - 1) { setSi(si + 1); setProgress(0); }
    else goNextGroup();
  }, [group, si, goNextGroup]);

  const goPrev = useCallback(() => {
    if (si > 0) { setSi(si - 1); setProgress(0); }
    else if (gi > 0) { const pg = groups[gi - 1]; setGi(gi - 1); setSi(Math.max(0, pg.stories.length - 1)); setProgress(0); }
    else setProgress(0);
  }, [si, gi, groups]);

  // mark viewed
  useEffect(() => {
    if (story && !readOnly) api.post(`/social/stories/${story.id}/view`).catch(() => {});
  }, [story?.id, readOnly]);

  // progress timer
  useEffect(() => {
    if (!story) return;
    setShowViewers(false); setViewers(null);
    elapsedBefore.current = 0;
    const durMs = (story.type === "video" ? (story.duration || 15) : (story.duration || 5)) * 1000;
    startedAt.current = performance.now();
    const tick = (now) => {
      if (paused) { startedAt.current = now - elapsedBefore.current; rafRef.current = requestAnimationFrame(tick); return; }
      let e = now - startedAt.current;
      if (story.type === "video" && videoRef.current && videoRef.current.duration) {
        const p = videoRef.current.currentTime / videoRef.current.duration;
        setProgress(Math.min(1, p));
        if (p >= 0.999) { goNext(); return; }
      } else {
        elapsedBefore.current = e;
        const p = e / durMs;
        setProgress(Math.min(1, p));
        if (p >= 1) { goNext(); return; }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id, paused]);

  // Kick off video playback. Android WebView (native app) frequently blocks
  // autoplay-with-sound → the frame stays black; if play() is rejected we surface
  // a tap-to-play button (the poster stays visible so it's never a black void).
  useEffect(() => {
    setVideoTap(false);
    if (story?.type !== "video") return;
    const id = setTimeout(() => {
      const v = videoRef.current;
      if (!v) return;
      const p = v.play();
      if (p && p.catch) p.catch(() => setVideoTap(true));
    }, 60);
    return () => clearTimeout(id);
  }, [story?.id, story?.type]);

  const react = async (emoji, silent = false) => {
    try { await api.post(`/social/stories/${story.id}/react`, { emoji }); if (!silent) toast.success(`${emoji}`); }
    catch { if (!silent) toast.error(t("st_failed")); }
  };
  const spawnHeart = (x, y) => {
    const id = Date.now() + Math.random();
    setHearts((h) => [...h, { id, x, y }]);
    setTimeout(() => setHearts((h) => h.filter((it) => it.id !== id)), 1100);
  };
  const sendReply = async () => {
    const txt = reply.trim(); if (!txt) return; setReply("");
    try { await api.post(`/social/stories/${story.id}/reply`, { text: txt }); toast.success(t("st_reply_sent")); }
    catch { toast.error(t("st_failed")); }
  };
  const del = async () => {
    if (!window.confirm(t("st_delete_story_q"))) return;
    try { await api.delete(`/social/stories/${story.id}`); toast.success(t("st_story_deleted")); onDeleted && onDeleted(story.id); onClose(); }
    catch { toast.error(t("st_failed")); }
  };
  const openViewers = async () => {
    setShowViewers(true); setPaused(true);
    try { const { data } = await api.get(`/social/stories/${story.id}/viewers`); setViewers(data); } catch {}
  };
  const openInsights = async () => {
    setShowInsights(true); setPaused(true); setInsights(null);
    try { const { data } = await api.get(`/social/stories/${story.id}/analytics`); setInsights(data); } catch {}
  };

  // seed sticker responses from the story payload on change
  useEffect(() => {
    if (!story) return;
    const mine = story.my_responses || {};
    const tallies = story.poll_tallies || {};
    const seed = {};
    (story.overlays || []).forEach((o) => { if (o.key) seed[o.key] = { value: mine[o.key], tally: tallies[o.key] || {} }; });
    setResp(seed); setQText({});
  }, [story?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // music playback
  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    a.muted = muted;
    if (story?.music && !readOnly) { a.play().catch(() => {}); }
    return () => { try { a.pause(); } catch {} };
  }, [story?.id, story?.music, muted, readOnly]);

  const respondPoll = async (key, idx) => {
    if (mine || readOnly) return;
    setResp((r) => ({ ...r, [key]: { ...(r[key] || {}), value: String(idx) } }));
    try { const { data } = await api.post(`/social/stories/${story.id}/respond`, { overlay_key: key, value: String(idx) });
      setResp((r) => ({ ...r, [key]: { value: String(idx), tally: data.tally } })); } catch { toast.error(t("st_failed")); }
  };
  const respondQuestion = async (key) => {
    const v = (qText[key] || "").trim(); if (!v || mine || readOnly) return;
    setQText((q) => ({ ...q, [key]: "" }));
    try { await api.post(`/social/stories/${story.id}/respond`, { overlay_key: key, value: v }); toast.success(t("st_sent"));
      setResp((r) => ({ ...r, [key]: { ...(r[key] || {}), value: v } })); } catch { toast.error(t("st_failed")); }
  };

  // gestures
  const onDown = (e) => { startPt.current = { x: e.clientX, y: e.clientY, t: Date.now() }; holdTimer.current = setTimeout(() => setPaused(true), 250); };
  const onUp = (e) => {
    clearTimeout(holdTimer.current);
    if (paused) { setPaused(false); startPt.current = null; return; }
    if (!startPt.current) return;
    const dx = e.clientX - startPt.current.x, dy = e.clientY - startPt.current.y;
    if (dy > 90 && Math.abs(dy) > Math.abs(dx)) { onClose(); return; }
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy)) { clearTimeout(tapTimer.current); dx < 0 ? goNextGroup() : goPrev(); return; }
    // tap — disambiguate single (navigate) vs double (send a heart)
    const now = Date.now();
    const isDouble = now - lastTap.current.t < 300 && Math.abs(e.clientX - lastTap.current.x) < 60;
    lastTap.current = { t: now, x: e.clientX };
    startPt.current = null;
    if (isDouble) {
      clearTimeout(tapTimer.current);
      spawnHeart(e.clientX, e.clientY);
      react("❤️", true);
      return;
    }
    const x = e.clientX, w = window.innerWidth;
    tapTimer.current = setTimeout(() => { if (x < w * 0.33) goPrev(); else goNext(); }, 270);
  };

  if (!story) return null;
  const bg = story.type === "text" ? bgByKey(story.bg) : null;
  const fontCss = STORY_FONTS.find((f) => f.key === story.text_style?.font)?.css;

  return createPortal(
    <div className="fixed inset-0 z-[120] bg-black flex items-center justify-center" data-testid="story-viewer">
      <div className="relative w-full h-full max-w-[440px] mx-auto bg-black overflow-hidden select-none"
        onPointerDown={onDown} onPointerUp={onUp}>
        {/* media */}
        {story.type === "text" ? (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center" style={{ background: bg.css }}>
            <span style={{ color: bg.fg, fontFamily: fontCss, fontSize: "28px", fontWeight: 700 }}>{story.text}</span>
          </div>
        ) : story.type === "video" ? (
          <video ref={videoRef} src={fileUrl(story.media)} poster={story.poster ? fileUrl(story.poster) : undefined}
            preload="auto" className="absolute inset-0 w-full h-full object-contain bg-black" autoPlay playsInline muted={muted}
            onPlaying={() => setVideoTap(false)} onError={() => setVideoTap(true)} />
        ) : (
          <img src={fileUrl(story.media)} alt="" className="absolute inset-0 w-full h-full object-contain bg-black" />
        )}
        {/* drawing layer */}
        {story.drawing && <img src={fileUrl(story.drawing)} alt="" className="absolute inset-0 w-full h-full object-contain pointer-events-none" data-testid="story-drawing" />}
        {/* double-tap floating hearts */}
        <AnimatePresence>
          {hearts.map((h) => (
            <motion.div key={h.id} data-testid="story-floating-heart"
              initial={{ opacity: 0, scale: 0.4, y: 0 }}
              animate={{ opacity: [0, 1, 1, 0], scale: [0.4, 1.25, 1, 1], y: -180 }}
              exit={{ opacity: 0 }} transition={{ duration: 1.1, ease: "easeOut" }}
              style={{ position: "fixed", left: h.x, top: h.y, zIndex: 130, pointerEvents: "none", color: "#ef4444" }}
              className="-translate-x-1/2 -translate-y-1/2 drop-shadow-lg">
              <Heart size={72} fill="currentColor" strokeWidth={0} />
            </motion.div>
          ))}
        </AnimatePresence>
        {/* tap-to-play (autoplay blocked in native WebView) */}
        {story.type === "video" && videoTap && (
          <button data-testid="story-tap-play"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              const v = videoRef.current; if (!v) return;
              const p = v.play(); if (p && p.catch) p.catch(() => {});
              setVideoTap(false);
            }}
            className="absolute inset-0 z-20 flex items-center justify-center bg-black/30">
            <span className="grid h-20 w-20 place-items-center rounded-full bg-black/55 border border-white/30 backdrop-blur-sm">
              <Play size={34} className="text-white translate-x-0.5" fill="white" />
            </span>
          </button>
        )}
        {/* overlays / interactive stickers */}
        {(story.overlays || []).map((o, i) => {
          const key = o.key || String(i);
          const r = resp[key] || {};
          const pos = { left: `${o.x * 100}%`, top: `${o.y * 100}%` };
          if (o.kind === "poll") {
            const tally = r.tally || {};
            const total = Object.values(tally).reduce((a, b) => a + b, 0);
            const voted = r.value != null && r.value !== undefined;
            return (
              <div key={key} className="absolute -translate-x-1/2 -translate-y-1/2 w-56" style={pos} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} data-testid={`story-poll-${key}`}>
                <div className="rounded-2xl bg-white/95 text-black p-3 shadow-xl">
                  <div className="text-sm font-bold mb-2 text-center break-words">{o.q}</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(o.options || []).map((opt, oi) => {
                      const c = tally[String(oi)] || 0;
                      const pct = total ? Math.round((c / total) * 100) : 0;
                      const chosen = r.value === String(oi);
                      return (
                        <button key={oi} onClick={() => respondPoll(key, oi)} disabled={mine || readOnly} data-testid={`poll-opt-${key}-${oi}`}
                          className={`relative overflow-hidden rounded-full py-1.5 text-xs font-semibold truncate px-2 ${chosen ? "ring-2 ring-[var(--rc-gold)]" : ""} ${voted ? "bg-black/5 text-[#8a6d1f]" : "bg-[var(--rc-gold)]/20 text-[#8a6d1f]"}`}>
                          {voted && <span className="absolute inset-y-0 left-0 bg-[var(--rc-gold)]/40" style={{ width: `${pct}%` }} />}
                          <span className="relative">{opt}{voted ? ` · ${pct}%` : ""}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }
          if (o.kind === "question") {
            const answered = r.value != null && r.value !== undefined;
            return (
              <div key={key} className="absolute -translate-x-1/2 -translate-y-1/2 w-56" style={pos} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} data-testid={`story-question-${key}`}>
                <div className="rounded-2xl bg-gradient-to-br from-[#F3D56D] to-[#D4AF37] p-3 shadow-xl text-black">
                  <div className="text-sm font-bold mb-2 text-center break-words flex items-center justify-center gap-1"><HelpCircle size={14} /> {o.q}</div>
                  {mine || readOnly ? <div className="rounded-lg bg-white/70 py-2 text-center text-[11px] text-black/50">{t("st_replies_in_insights")}</div>
                    : answered ? <div className="rounded-lg bg-white/80 py-2 text-center text-xs font-semibold">{t("st_sent")}</div>
                    : <div className="flex gap-1.5">
                        <input value={qText[key] || ""} onChange={(e) => setQText((q) => ({ ...q, [key]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && respondQuestion(key)} placeholder={t("st_type_short")} data-testid={`question-input-${key}`} className="flex-1 min-w-0 rounded-lg bg-white/90 px-2.5 py-1.5 text-xs outline-none text-black" />
                        <button onClick={() => respondQuestion(key)} data-testid={`question-send-${key}`} className="h-8 w-8 grid place-items-center rounded-lg bg-black text-[var(--rc-gold)]"><Send size={14} /></button>
                      </div>}
                </div>
              </div>
            );
          }
          if (o.kind === "link") {
            return (
              <a key={key} href={o.url} target="_blank" rel="noreferrer" style={pos} data-testid={`story-link-${key}`}
                onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1.5 rounded-full bg-white/95 text-black px-3.5 py-2 shadow-xl text-sm font-semibold">
                <Link2 size={15} className="text-[#8a6d1f]" /> {o.label || "Visit link"}
              </a>
            );
          }
          return (
            <div key={key} className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={pos}>
              {o.kind === "emoji" ? <span style={{ fontSize: `${o.size}px` }}>{o.value}</span>
                : <span style={{ color: o.color, fontFamily: o.font, fontSize: `${o.size}px`, fontWeight: 700, textShadow: "0 1px 6px rgba(0,0,0,.5)" }}>{o.value}</span>}
            </div>
          );
        })}
        {/* music */}
        {story.music && !readOnly && <audio ref={audioRef} src={fileUrl(story.music)} loop autoPlay />}
        {story.music && (
          <button onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }} data-testid="story-music-toggle"
            onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}
            className="absolute top-16 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-3 py-1 text-white text-xs">
            {muted ? <VolumeX size={12} /> : <Volume2 size={12} className="text-[var(--rc-gold)]" />} <Music size={11} /> <span className="truncate max-w-[150px]">{story.music_title || "Audio"}</span>
          </button>
        )}
        {/* caption for media */}
        {story.type !== "text" && story.text && (
          <div className="absolute left-0 right-0 bottom-28 px-6 text-center pointer-events-none">
            <span className="inline-block bg-black/40 rounded-lg px-3 py-1.5 text-white text-sm">{story.text}</span>
          </div>
        )}

        {/* progress bars */}
        <div className="absolute top-2 left-2 right-2 flex gap-1">
          {group.stories.map((s, i) => (
            <div key={s.id} className="flex-1 h-[3px] rounded-full bg-[var(--rc-silver)]/30 overflow-hidden">
              <div className="h-full bg-[var(--rc-gold)]" style={{ width: `${i < si ? 100 : i === si ? progress * 100 : 0}%` }} />
            </div>
          ))}
        </div>

        {/* header */}
        <div className="absolute top-5 left-2 right-2 flex items-center gap-2 px-1">
          <div className="h-8 w-8 rounded-full grid place-items-center text-xs font-semibold overflow-hidden border border-white/40 text-white" style={{ backgroundColor: group.author_avatar ? "#000" : avatarColor(group.author_name) }}>
            {group.author_avatar ? <img src={group.author_avatar} alt="" className="h-full w-full object-cover" /> : getInitials(group.author_name)}
          </div>
          <span className="text-white text-sm font-medium drop-shadow">{mine ? t("st_your_story") : group.author_name}</span>
          <span className="text-white/60 text-xs">{dayjs(story.created_at).fromNow()}</span>
          <div className="ml-auto flex items-center gap-1">
            {mine && !readOnly && <button onClick={(e) => { e.stopPropagation(); del(); }} data-testid="story-delete-btn" className="h-8 w-8 grid place-items-center rounded-full bg-black/30 text-white"><Trash2 size={17} /></button>}
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="story-viewer-close" className="h-8 w-8 grid place-items-center rounded-full bg-black/30 text-white"><X size={19} /></button>
          </div>
        </div>

        {/* bottom bar */}
        {!readOnly && (
        <div className="absolute left-0 right-0 bottom-0 p-3 rc-safe-bottom" onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}>
          {mine ? (
            <div className="flex items-center gap-2">
              <button onClick={openViewers} data-testid="story-viewers-btn" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-black/40 text-white py-2.5 text-sm">
                <Eye size={16} className="text-[var(--rc-gold)]" /> {story.view_count} {story.view_count === 1 ? t("st_view") : t("st_views")}
              </button>
              <button onClick={openInsights} data-testid="story-insights-btn" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-2.5 text-sm">
                <BarChart3 size={16} /> {t("st_insights")}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()}
                placeholder={`${t("st_reply_to")} ${group.author_name?.split(" ")[0]}…`} data-testid="story-reply-input"
                className="flex-1 min-w-0 rounded-full bg-white/15 border border-white/20 text-white px-4 py-2.5 text-sm outline-none placeholder-white/50" />
              <div className="flex items-center gap-1">
                {REACT_EMOJIS.slice(0, 3).map((em) => <button key={em} onClick={() => react(em)} data-testid={`story-react-${em}`} className="text-xl">{em}</button>)}
              </div>
              {reply.trim()
                ? <button onClick={sendReply} data-testid="story-reply-send" className="h-10 w-10 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black shrink-0"><Send size={17} /></button>
                : <button onClick={() => react("❤️")} data-testid="story-react-heart" className="h-10 w-10 grid place-items-center rounded-full bg-white/15 text-white shrink-0"><Heart size={18} /></button>}
            </div>
          )}
        </div>
        )}

        {/* viewers sheet */}
        {showViewers && (
          <div className="absolute inset-0 bg-black/70 flex flex-col justify-end" data-testid="story-viewers-sheet" onPointerDown={(e) => { e.stopPropagation(); }} onPointerUp={(e) => e.stopPropagation()}>
            <div className="bg-[var(--rc-surface)] rounded-t-2xl max-h-[70%] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-[var(--rc-border)]">
                <div className="flex items-center gap-3">
                  <span className="font-heading text-lg text-[var(--rc-text)]">{viewers ? `${viewers.count} ${viewers.count === 1 ? t("st_view") : t("st_views")}` : t("st_viewers")}</span>
                  {viewers && viewers.viewers.filter((v) => v.reaction).length > 0 && (
                    <span className="flex items-center gap-1 text-sm text-[var(--rc-gold)]" data-testid="story-viewers-reaction-count">
                      <Heart size={14} fill="currentColor" strokeWidth={0} /> {viewers.viewers.filter((v) => v.reaction).length}
                    </span>
                  )}
                </div>
                <button onClick={() => { setShowViewers(false); setPaused(false); }} className="text-[var(--rc-muted)]"><X size={20} /></button>
              </div>
              <div className="overflow-y-auto no-scrollbar p-2">
                {!viewers ? <div className="p-6 text-center text-[var(--rc-muted)]">…</div>
                  : viewers.viewers.length === 0 ? <div className="p-6 text-center text-[var(--rc-muted)]">{t("st_no_viewers")}</div>
                    : viewers.viewers.map((v) => (
                      <div key={v.id} className="flex items-center gap-3 px-3 py-2" data-testid={`story-viewer-row-${v.id}`}>
                        <div className="h-9 w-9 rounded-full grid place-items-center text-xs font-semibold overflow-hidden text-[var(--rc-gold)]" style={{ backgroundColor: v.avatar ? "var(--rc-bg)" : avatarColor(v.name) }}>
                          {v.avatar ? <img src={v.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(v.name)}
                        </div>
                        <div className="flex-1 min-w-0"><div className="text-sm text-[var(--rc-text)] truncate">{v.name}</div><div className="text-xs text-[var(--rc-muted)]">{dayjs(v.at).fromNow()}</div></div>
                        {v.reaction && <span className="text-lg" data-testid="story-viewer-reaction">{v.reaction}</span>}
                      </div>
                    ))}
              </div>
            </div>
          </div>
        )}

        {/* insights sheet */}
        {showInsights && (
          <div className="absolute inset-0 bg-black/70 flex flex-col justify-end" data-testid="story-insights-sheet" onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}>
            <div className="bg-[var(--rc-surface)] rounded-t-2xl max-h-[80%] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-[var(--rc-border)]">
                <span className="font-heading text-lg text-[var(--rc-gold)]">{t("st_story_insights")}</span>
                <button onClick={() => { setShowInsights(false); setPaused(false); }} data-testid="insights-close" className="text-[var(--rc-muted)]"><X size={20} /></button>
              </div>
              <div className="overflow-y-auto no-scrollbar p-4 space-y-4">
                {!insights ? <div className="py-8 text-center text-[var(--rc-muted)]">…</div> : (<>
                  <div className="grid grid-cols-3 gap-2">
                    {[["views", t("st_views"), insights.views], ["replies", t("st_replies"), insights.replies], ["reactions", t("st_reactions"), insights.reactions]].map(([id, l, v]) => (
                      <div key={id} className="rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-3 text-center" data-testid={`insight-${id}`}>
                        <div className="text-2xl font-heading text-[var(--rc-gold)]">{v}</div>
                        <div className="text-[11px] text-[var(--rc-muted)]">{l}</div>
                      </div>
                    ))}
                  </div>
                  {Object.keys(insights.reaction_breakdown || {}).length > 0 && (
                    <div className="flex items-center gap-3 flex-wrap text-sm">
                      {Object.entries(insights.reaction_breakdown).map(([em, c]) => <span key={em} className="flex items-center gap-1"><span className="text-lg">{em}</span> {c}</span>)}
                    </div>
                  )}
                  {(insights.stickers || []).map((st) => (
                    <div key={st.key} className="rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-3">
                      <div className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                        {st.kind === "poll" ? <BarChart3 size={14} className="text-[var(--rc-gold)]" /> : <HelpCircle size={14} className="text-[var(--rc-gold)]" />}
                        {st.q} <span className="text-[var(--rc-muted)] font-normal">· {st.total}</span>
                      </div>
                      {st.kind === "poll" ? (
                        <div className="space-y-1.5">
                          {(st.options || []).map((opt, i) => {
                            const c = (st.counts || [])[i] || 0; const pct = st.total ? Math.round((c / st.total) * 100) : 0;
                            return (
                              <div key={i} className="relative rounded-full bg-[var(--rc-surface)] overflow-hidden text-xs h-7">
                                <div className="absolute inset-y-0 left-0 bg-[var(--rc-gold)]/30" style={{ width: `${pct}%` }} />
                                <div className="relative flex items-center justify-between px-3 h-full"><span className="truncate">{opt}</span><span className="font-semibold">{pct}%</span></div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          {(st.answers || []).length === 0 ? <div className="text-xs text-[var(--rc-muted)]">{t("st_no_answers")}</div>
                            : st.answers.map((a, i) => (
                              <div key={i} className="text-xs"><span className="text-[var(--rc-gold)] font-semibold">{a.name}: </span>{a.text}</div>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>)}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}
